import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import {
  createSeedVarietyCategory,
  deleteSeedVarietyCategory,
  fetchSeedVarietiesAdmin,
  getToken,
  updateSeedVarietyCategory,
  uploadFile,
} from './api';
import { AdminImagePreview, AdminModal, fileInput, input, label } from './adminUi';

function emptyBlock() {
  const d = DEFAULT_SITE.seedVarieties;
  return {
    categories: Array.isArray(d?.categories) ? [...d.categories] : [],
    families: Array.isArray(d?.families) ? [...d.families] : [],
  };
}

function emptyCategoryDraft() {
  return { name: '', summary: '', heroImage: '' };
}

const secondaryBtnClass =
  'rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50';
const primaryForestClass =
  'rounded-lg bg-brand-forest px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50';
const dangerBtnClass =
  'rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50';

function ModalFooter({ onCancel, onSave, saveLabel, saving }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" onClick={onCancel} className={secondaryBtnClass} disabled={saving}>
        Cancel
      </button>
      <button type="button" onClick={onSave} disabled={saving} className={primaryForestClass}>
        {saving ? 'Saving…' : saveLabel}
      </button>
    </div>
  );
}

export default function SeedVarietyCategoriesDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [block, setBlock] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryModalMode, setCategoryModalMode] = useState(null);
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [modalErr, setModalErr] = useState('');
  const [modalSaving, setModalSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSeedVarietiesAdmin()
      .then((b) => setBlock(b && typeof b === 'object' ? b : emptyBlock()))
      .catch(() => setBlock(emptyBlock()));
  }, [navigate]);

  const openCreateModal = useCallback(() => {
    setModalErr('');
    setDraft(emptyCategoryDraft());
    setEditCategoryId(null);
    setCategoryModalMode('create');
    setCategoryModalOpen(true);
  }, []);

  const openEditModal = useCallback((cat) => {
    if (!cat) return;
    setModalErr('');
    setDraft({
      name: cat.name || '',
      summary: typeof cat.summary === 'string' ? cat.summary : '',
      heroImage: typeof cat.heroImage === 'string' ? cat.heroImage : '',
    });
    setEditCategoryId(cat.id);
    setCategoryModalMode('edit');
    setCategoryModalOpen(true);
  }, []);

  const closeCategoryModal = useCallback(() => {
    setCategoryModalOpen(false);
    setCategoryModalMode(null);
    setEditCategoryId(null);
    setDraft(null);
    setModalErr('');
    setModalSaving(false);
    setUploadingHero(false);
  }, []);

  useEffect(() => {
    if (!block) return;
    const s = location.state;
    if (!s || typeof s !== 'object') return;
    if (s.openNewCategory) {
      openCreateModal();
      navigate(location.pathname, { replace: true, state: {} });
    } else if (typeof s.editCategoryId === 'string' && s.editCategoryId) {
      const c = block.categories?.find((x) => x.id === s.editCategoryId);
      if (c) openEditModal(c);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [block, location.pathname, location.state, navigate, openCreateModal, openEditModal]);

  async function onHeroUpload(file) {
    if (!file) return;
    setErr('');
    setModalErr('');
    setUploadingHero(true);
    try {
      const { url } = await uploadFile(file);
      setDraft((d) => (d ? { ...d, heroImage: url } : d));
    } catch (e) {
      const m = e.message || 'Upload failed';
      setErr(m);
      setModalErr(m);
    } finally {
      setUploadingHero(false);
    }
  }

  async function handleModalSave() {
    if (!draft) return;
    const name = String(draft.name || '').trim();
    if (!name) {
      setModalErr('Name is required.');
      return;
    }
    const payload = {
      name,
      summary: String(draft.summary || ''),
      heroImage: String(draft.heroImage || '').trim(),
    };
    setModalErr('');
    setModalSaving(true);
    try {
      if (categoryModalMode === 'create') {
        const { seedVarieties } = await createSeedVarietyCategory(payload);
        setBlock(seedVarieties && typeof seedVarieties === 'object' ? seedVarieties : emptyBlock());
        setMsg('Category created.');
      } else if (categoryModalMode === 'edit' && editCategoryId) {
        const next = await updateSeedVarietyCategory(editCategoryId, payload);
        setBlock(next && typeof next === 'object' ? next : emptyBlock());
        setMsg('Category updated.');
      }
      closeCategoryModal();
    } catch (e) {
      setModalErr(e.message || 'Save failed');
    } finally {
      setModalSaving(false);
    }
  }

  async function handleModalDelete() {
    if (!editCategoryId) return;
    if (!window.confirm('Delete this category? Products must be moved or deleted first.')) return;
    setModalSaving(true);
    setModalErr('');
    try {
      const next = await deleteSeedVarietyCategory(editCategoryId);
      setBlock(next && typeof next === 'object' ? next : emptyBlock());
      setMsg('Category removed.');
      closeCategoryModal();
    } catch (e) {
      setModalErr(e.message || 'Delete failed');
    } finally {
      setModalSaving(false);
    }
  }

  async function removeCategory(cat) {
    if (!window.confirm(`Delete category “${cat.name || cat.id}”?`)) return;
    setMsg('');
    setErr('');
    setBusy(true);
    try {
      const next = await deleteSeedVarietyCategory(cat.id);
      setBlock(next && typeof next === 'object' ? next : emptyBlock());
      setMsg('Category removed.');
    } catch (e) {
      setErr(e.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  if (!block) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-neutral-600">Loading…</div>
    );
  }

  const { categories } = block;
  const productCountByCategory = (categoryId) =>
    block.families.filter((f) => f.categoryId === categoryId).length;

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <Link
            to="/admin/dashboard/seed-varieties"
            className="text-xs font-medium text-brand-olive hover:underline"
          >
            ← Variety specs
          </Link>
          <h2 className="mt-1 text-lg font-bold text-neutral-900 md:text-xl">Variety categories</h2>
        </div>
        <button type="button" onClick={openCreateModal} className={primaryForestClass}>
          + New category
        </button>
      </header>

      <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {msg ? <p className="mb-4 text-sm text-green-700">{msg}</p> : null}
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}

        {categories.length === 0 ? (
          <p className="text-sm text-neutral-600">
            No categories yet.{' '}
            <button type="button" onClick={openCreateModal} className="font-semibold text-brand-olive hover:underline">
              Create one
            </button>
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
            <table className="w-full min-w-[560px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50">
                  <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">Name</th>
                  <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">URL id</th>
                  <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">Products</th>
                  <th className="w-[1%] whitespace-nowrap px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/90">
                    <td className="px-3 py-2 align-middle font-medium text-neutral-900">{c.name || '—'}</td>
                    <td className="px-3 py-2 align-middle font-mono text-xs text-neutral-600">{c.id}</td>
                    <td className="px-3 py-2 align-middle text-neutral-600">{productCountByCategory(c.id)}</td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-right">
                      <div className="inline-flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEditModal(c)}
                          className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-800 hover:bg-neutral-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                          onClick={() => removeCategory(c)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <AdminModal
        open={categoryModalOpen}
        title={categoryModalMode === 'create' ? 'New category' : 'Edit category'}
        description={
          categoryModalMode === 'edit' && editCategoryId
            ? `URL id: ${editCategoryId} (cannot be changed here)`
            : 'Name, summary, and optional hero image for the public category page.'
        }
        error={modalErr}
        onClose={closeCategoryModal}
        footer={
          <div className="flex flex-wrap items-center justify-between gap-2">
            {categoryModalMode === 'edit' ? (
              <button
                type="button"
                disabled={modalSaving}
                onClick={handleModalDelete}
                className={dangerBtnClass}
              >
                {modalSaving ? '…' : 'Delete category'}
              </button>
            ) : (
              <span />
            )}
            <ModalFooter
              onCancel={closeCategoryModal}
              onSave={handleModalSave}
              saveLabel={categoryModalMode === 'create' ? 'Create category' : 'Save changes'}
              saving={modalSaving}
            />
          </div>
        }
      >
        {draft ? (
          <div className="space-y-4">
            <label className={label}>
              Name
              <input
                className={input}
                value={draft.name}
                onChange={(e) => {
                  setModalErr('');
                  setDraft((d) => ({ ...d, name: e.target.value }));
                }}
                placeholder="e.g. Tomato"
              />
            </label>
            <label className={label}>
              Summary
              <textarea
                className={`${input} min-h-[4rem]`}
                value={draft.summary}
                onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))}
                placeholder="Short description for the category page hero (optional)"
              />
            </label>
            <div>
              <p className="text-sm font-medium text-neutral-700">Category hero image</p>
              <p className="mt-0.5 text-xs text-neutral-500">Large background on the public crop category page. URL or upload.</p>
              <AdminImagePreview src={draft.heroImage} alt="" emptyLabel="No hero image" />
              <input
                className={`${input} mt-2`}
                value={draft.heroImage}
                onChange={(e) => {
                  setModalErr('');
                  setDraft((d) => ({ ...d, heroImage: e.target.value }));
                }}
                placeholder="https://… or path after upload"
              />
              <input
                type="file"
                accept="image/*"
                disabled={uploadingHero}
                className={`${fileInput} mt-2`}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  onHeroUpload(f);
                }}
              />
              {uploadingHero ? <p className="mt-1 text-xs text-neutral-500">Uploading…</p> : null}
              {draft.heroImage ? (
                <button
                  type="button"
                  onClick={() => setDraft((d) => (d ? { ...d, heroImage: '' } : d))}
                  className="mt-2 text-xs font-medium text-neutral-600 underline decoration-neutral-300 hover:text-neutral-900"
                >
                  Clear hero image URL
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </AdminModal>
    </>
  );
}
